import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import useDarkMode from "../../../hooks/useDarkMode";
import showErrorToast, {
  showSuccessToast,
} from "../toast/toast";
import {
  isNiimbotSupported,
  isNiimbotConnected,
  connectNiimbot,
  disconnectNiimbot,
  buildTableQrLabelCanvas,
  printLabelCanvas,
  formatNiimbotConnectError,
  downloadCanvasPng,
  resolveLabelSize,
  simulatePrintDensityPreview,
  DEFAULT_PRINT_DENSITY,
  LABEL_SIZE_PRESETS,
  DEFAULT_LABEL_SIZE_ID,
} from "../../../utils/niimbotPrinter";
import "../TableQr/tableQrModal.scss";

/**
 * Collection / takeaway online-ordering QR: copy link, download PNG, print via Niim Bot.
 */
function CollectionQrSection({
  compId,
  collectionEnabled,
  themeColor,
}) {
  const isDarkMode = useDarkMode();
  const authUser = useSelector((state) => state.auth.user?.userData);
  const [qrUrl, setQrUrl] = useState("");
  const [companySlug, setCompanySlug] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [printerBusy, setPrinterBusy] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [showPairGuide, setShowPairGuide] = useState(false);
  const [showAllBleDevices, setShowAllBleDevices] = useState(false);
  const [pendingPrint, setPendingPrint] = useState(false);
  const [labelSizeId, setLabelSizeId] = useState(DEFAULT_LABEL_SIZE_ID);
  const [customWMm, setCustomWMm] = useState(50);
  const [customHMm, setCustomHMm] = useState(30);
  const [density, setDensity] = useState(DEFAULT_PRINT_DENSITY);
  const [showCollectionTitle, setShowCollectionTitle] = useState(true);
  const [showCompanyName, setShowCompanyName] = useState(true);
  const [showScanHint, setShowScanHint] = useState(true);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const pairGuideRef = useRef(null);
  const previewGenRef = useRef(0);
  const previewCanvasRef = useRef(null);

  const niimSupport = useMemo(() => isNiimbotSupported(), []);

  const activePreset = useMemo(
    () =>
      LABEL_SIZE_PRESETS.find((p) => p.id === labelSizeId) ||
      LABEL_SIZE_PRESETS[0],
    [labelSizeId]
  );

  const labelMm = useMemo(() => {
    if (activePreset.custom) {
      return { wMm: customWMm, hMm: customHMm };
    }
    return { wMm: activePreset.wMm, hMm: activePreset.hMm };
  }, [activePreset, customWMm, customHMm]);

  const labelPx = useMemo(
    () => resolveLabelSize(labelMm.wMm, labelMm.hMm),
    [labelMm.wMm, labelMm.hMm]
  );

  const fetchLink = useCallback(async () => {
    if (!compId) return;
    setLoading(true);
    setLoadError("");
    try {
      const response = await axiosInstance.get(APIs.BO_GET_COLLECTION_QR_LINK, {
        params: { CompID: compId },
      });
      const data = response?.data?.data ?? response?.data?.Data ?? null;
      const url = data?.qrUrl ?? data?.QrUrl ?? "";
      const slug = data?.companySlug ?? data?.CompanySlug ?? "";
      if (!url) {
        setQrUrl("");
        setCompanySlug("");
        setLoadError(
          "Unable to build collection link. Ensure the company has an online ordering slug."
        );
        return;
      }
      setQrUrl(url);
      setCompanySlug(slug);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        "Failed to load collection QR link.";
      setQrUrl("");
      setCompanySlug("");
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [compId]);

  const loadCompanyName = useCallback(async () => {
    if (!compId) return;
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE, {
        params: { CompID: compId },
      });
      const data = response?.data?.data ?? response?.data?.Data ?? response?.data;
      const name =
        data?.compName ??
        data?.CompName ??
        data?.companyName ??
        data?.CompanyName ??
        authUser?.compName ??
        authUser?.CompName ??
        "";
      setCompanyName(String(name || "").trim());
    } catch {
      setCompanyName(
        String(authUser?.compName || authUser?.CompName || "").trim()
      );
    }
  }, [compId, authUser]);

  useEffect(() => {
    if (collectionEnabled && compId) {
      fetchLink();
      loadCompanyName();
      setPrinterConnected(isNiimbotConnected());
    } else {
      setQrUrl("");
      setCompanySlug("");
      setLoadError("");
      setShowPairGuide(false);
      setPendingPrint(false);
      setPreviewUrl("");
    }
  }, [collectionEnabled, compId, fetchLink, loadCompanyName]);

  useEffect(() => {
    if (!showPairGuide) return undefined;
    const timer = window.setTimeout(() => {
      pairGuideRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [showPairGuide]);

  const labelOptions = useMemo(
    () => ({
      wMm: labelMm.wMm,
      hMm: labelMm.hMm,
      showTableName: showCollectionTitle,
      showServiceArea: false,
      showCompanyName: showCompanyName && Boolean(companyName),
      showScanHint,
      companyName: companyName || "",
    }),
    [
      labelMm.wMm,
      labelMm.hMm,
      showCollectionTitle,
      showCompanyName,
      showScanHint,
      companyName,
    ]
  );

  useEffect(() => {
    previewGenRef.current += 1;
    const thisGen = previewGenRef.current;
    let timer = null;

    const applyPreview = async () => {
      if (!qrUrl) {
        if (thisGen !== previewGenRef.current) return;
        setPreviewUrl("");
        setPreviewBusy(false);
        return;
      }
      setPreviewBusy(true);
      const build = () =>
        buildTableQrLabelCanvas({
          qrUrl,
          tableName: "Collection",
          ...labelOptions,
        });

      try {
        let canvas;
        try {
          canvas = await build();
        } catch {
          canvas = await build();
        }
        if (thisGen !== previewGenRef.current) return;
        previewCanvasRef.current = canvas;
        const previewCanvas = simulatePrintDensityPreview(canvas, density);
        setPreviewUrl(previewCanvas.toDataURL("image/png"));
      } catch {
        if (thisGen === previewGenRef.current && !previewCanvasRef.current) {
          setPreviewUrl("");
        }
      } finally {
        if (thisGen === previewGenRef.current) setPreviewBusy(false);
      }
    };

    timer = window.setTimeout(applyPreview, 120);
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [qrUrl, labelOptions, density]);

  const handleCopyLink = async () => {
    if (!qrUrl) return;
    try {
      await navigator.clipboard.writeText(qrUrl);
      showSuccessToast("Collection ordering link copied.");
    } catch {
      showErrorToast("Could not copy link.");
    }
  };

  const buildCollectionLabel = useCallback(async () => {
    return buildTableQrLabelCanvas({
      qrUrl,
      tableName: "Collection",
      ...labelOptions,
    });
  }, [qrUrl, labelOptions]);

  const handleDownloadQr = async () => {
    if (!qrUrl) return;
    try {
      const canvas = await buildCollectionLabel();
      const nameSlug = (companySlug || "collection")
        .replace(/[^\w\-]+/g, "-")
        .toLowerCase();
      downloadCanvasPng(
        canvas,
        `${nameSlug}-collection-${labelMm.wMm}x${labelMm.hMm}.png`
      );
      showSuccessToast("QR label downloaded.");
    } catch (err) {
      showErrorToast(err?.message || "Could not download QR label.");
    }
  };

  const printCollectionLabel = useCallback(
    async (client) => {
      const canvas = await buildCollectionLabel();
      await printLabelCanvas(client, canvas, 1, { density });
      showSuccessToast("Label sent to Niim Bot");
    },
    [buildCollectionLabel, density]
  );

  const startNiimPrint = async () => {
    if (!qrUrl) {
      showErrorToast("Nothing to print");
      return;
    }
    if (!niimSupport.supported) {
      showErrorToast(niimSupport.message);
      return;
    }
    if (!isNiimbotConnected()) {
      setPendingPrint(true);
      setShowPairGuide(true);
      return;
    }
    setPrinterBusy(true);
    try {
      const client = await connectNiimbot();
      setPrinterConnected(true);
      await printCollectionLabel(client);
    } catch (err) {
      setPrinterConnected(isNiimbotConnected());
      showErrorToast(formatNiimbotConnectError(err));
    } finally {
      setPrinterBusy(false);
    }
  };

  const handleConfirmPair = async () => {
    setPrinterBusy(true);
    try {
      const client = await connectNiimbot({
        showAllDevices: showAllBleDevices,
      });
      setPrinterConnected(true);
      setShowPairGuide(false);
      if (pendingPrint) {
        await printCollectionLabel(client);
      } else {
        showSuccessToast("Niim Bot connected");
      }
    } catch (err) {
      setPrinterConnected(isNiimbotConnected());
      showErrorToast(formatNiimbotConnectError(err));
    } finally {
      setPrinterBusy(false);
      setPendingPrint(false);
      setShowAllBleDevices(false);
    }
  };

  const handleDisconnect = async () => {
    setPrinterBusy(true);
    try {
      await disconnectNiimbot();
      setPrinterConnected(false);
      showSuccessToast("Niim Bot disconnected");
    } catch (err) {
      showErrorToast(formatNiimbotConnectError(err));
    } finally {
      setPrinterBusy(false);
    }
  };

  if (!collectionEnabled) {
    return (
      <div className="card settings-section border-0 mt-3">
        <div className="list-group list-group-flush">
          <div className="list-group-item">
            <div className="d-flex align-items-center mb-2">
              <i
                className="fas fa-qrcode me-2"
                style={{ color: themeColor }}
              ></i>
              <span className="fw-bold">Collection ordering QR</span>
            </div>
            <p className="text-muted small mb-0">
              Turn on Collection above to download a QR code customers can scan
              for takeaway / collection ordering.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const studioClass = `table-qr-modal${
    isDarkMode ? " table-qr-modal--dark" : " table-qr-modal--light"
  }`;

  return (
    <div className="card settings-section border-0 mt-3">
      <div className="list-group list-group-flush">
        <div className="list-group-item">
          <div className="d-flex align-items-center mb-2">
            <i
              className="fas fa-qrcode me-2"
              style={{ color: themeColor }}
            ></i>
            <span className="fw-bold">Collection ordering QR</span>
          </div>
          <p className="text-muted small mb-3">
            Customers scan this QR to open online ordering with collection
            (takeaway) already selected.
          </p>

          {loading ? (
            <div className="text-muted small">
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Loading link…
            </div>
          ) : loadError ? (
            <div className="alert alert-warning py-2 mb-0" role="alert">
              {loadError}
              <button
                type="button"
                className="btn btn-sm btn-link ms-2 p-0"
                onClick={fetchLink}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className={studioClass} style={{ "--tqr-theme": themeColor }}>
              <div className="row g-3 align-items-start">
                <div className="col-12 col-md-auto">
                  <div className="table-qr-modal__studio-side" style={{ position: "static" }}>
                    <div className="table-qr-modal__preview-meta small text-muted mb-1">
                      Preview · {labelMm.wMm} × {labelMm.hMm} mm · density{" "}
                      {density}
                    </div>
                    <div className="table-qr-modal__preview-frame">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Collection QR label preview"
                          className={
                            previewBusy
                              ? "table-qr-modal__preview-img--busy"
                              : undefined
                          }
                        />
                      ) : previewBusy ? (
                        <div className="table-qr-modal__spinner" />
                      ) : (
                        <p className="table-qr-modal__empty-text mb-0">
                          No preview
                        </p>
                      )}
                      {previewBusy && previewUrl && (
                        <div
                          className="table-qr-modal__preview-busy"
                          aria-hidden
                        >
                          <div className="table-qr-modal__spinner" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col">
                  <div className="text-muted small mb-1">Link</div>
                  <div
                    className="small mb-3 text-break"
                    style={{ wordBreak: "break-all" }}
                  >
                    {qrUrl || "—"}
                  </div>

                  <div className="table-qr-modal__section-title mb-2">
                    <span className="table-qr-modal__section-bar" aria-hidden />
                    Label
                  </div>
                  <div
                    className="table-qr-modal__size-row"
                    role="group"
                    aria-label="Label size"
                  >
                    {LABEL_SIZE_PRESETS.map((preset) => {
                      const active = labelSizeId === preset.id;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          className={`table-qr-modal__size-chip${
                            active ? " table-qr-modal__size-chip--active" : ""
                          }`}
                          onClick={() => setLabelSizeId(preset.id)}
                          disabled={printerBusy}
                        >
                          {active && <i className="fa fa-check" aria-hidden />}
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>

                  {activePreset.custom && (
                    <div className="table-qr-modal__custom-size">
                      <label>
                        W (mm)
                        <input
                          type="number"
                          min={10}
                          max={100}
                          value={customWMm}
                          onChange={(e) =>
                            setCustomWMm(Number(e.target.value) || 50)
                          }
                          disabled={printerBusy}
                        />
                      </label>
                      <label>
                        H (mm)
                        <input
                          type="number"
                          min={10}
                          max={100}
                          value={customHMm}
                          onChange={(e) =>
                            setCustomHMm(Number(e.target.value) || 30)
                          }
                          disabled={printerBusy}
                        />
                      </label>
                    </div>
                  )}

                  <div className="table-qr-modal__density">
                    <span>Density</span>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      step={1}
                      value={density}
                      onChange={(e) => setDensity(Number(e.target.value))}
                      disabled={printerBusy}
                      aria-label="Print density"
                    />
                    <strong>{density}</strong>
                  </div>
                  <p className="table-qr-modal__density-hint">
                    Niim Bot print heat — lower looks lighter, higher darker.
                    {` · ${labelMm.wMm} × ${labelMm.hMm} mm · ${labelPx.wPx}×${labelPx.hPx} px`}
                  </p>

                  <div className="table-qr-modal__section-title mt-3 mb-2">
                    <span className="table-qr-modal__section-bar" aria-hidden />
                    Label text
                  </div>
                  <div className="table-qr-modal__checks">
                    <label>
                      <input
                        type="checkbox"
                        checked={showCollectionTitle}
                        onChange={(e) =>
                          setShowCollectionTitle(e.target.checked)
                        }
                        disabled={printerBusy}
                      />
                      &quot;Collection&quot; title
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={showCompanyName}
                        onChange={(e) => setShowCompanyName(e.target.checked)}
                        disabled={printerBusy || !companyName}
                      />
                      Restaurant name
                      {companyName ? (
                        <span className="text-muted ms-1">({companyName})</span>
                      ) : (
                        <span className="text-muted ms-1">(not available)</span>
                      )}
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={showScanHint}
                        onChange={(e) => setShowScanHint(e.target.checked)}
                        disabled={printerBusy}
                      />
                      &quot;Scan for options&quot;
                    </label>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button
                      type="button"
                      className="btn settings-btn settings-btn--ghost"
                      onClick={handleCopyLink}
                      disabled={!qrUrl || printerBusy}
                    >
                      <i className="fas fa-copy me-1"></i>
                      Copy link
                    </button>
                    <button
                      type="button"
                      className="btn settings-btn settings-btn--primary"
                      style={{
                        backgroundColor: themeColor,
                        color: "white",
                        border: "none",
                      }}
                      onClick={handleDownloadQr}
                      disabled={!qrUrl || printerBusy}
                    >
                      <i className="fas fa-download me-1"></i>
                      Download QR
                    </button>
                    <button
                      type="button"
                      className="btn settings-btn settings-btn--ghost"
                      onClick={startNiimPrint}
                      disabled={!qrUrl || printerBusy || !niimSupport.supported}
                      title={
                        niimSupport.supported
                          ? "Print label to Niim Bot"
                          : niimSupport.message
                      }
                    >
                      <i className="fas fa-print me-1"></i>
                      {printerBusy ? "Printing…" : "Print to Niim Bot"}
                    </button>
                    {printerConnected ? (
                      <button
                        type="button"
                        className="btn settings-btn settings-btn--ghost"
                        onClick={handleDisconnect}
                        disabled={printerBusy}
                      >
                        Disconnect
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn settings-btn settings-btn--ghost"
                        disabled={!niimSupport.supported || printerBusy}
                        onClick={() => {
                          setPendingPrint(false);
                          setShowPairGuide(true);
                        }}
                      >
                        Connect Niim Bot
                      </button>
                    )}
                  </div>
                  {!niimSupport.supported && (
                    <p className="text-muted small mt-2 mb-0">
                      {niimSupport.message}
                    </p>
                  )}
                </div>
              </div>

              {showPairGuide && (
                <div
                  ref={pairGuideRef}
                  className="table-qr-modal__pair-guide mt-3"
                  role="region"
                  aria-label="Pair Niim Bot"
                >
                  <div className="table-qr-modal__pair-guide-icon" aria-hidden>
                    <i className="fa fa-bluetooth-b" />
                  </div>
                  <div className="table-qr-modal__pair-guide-body">
                    <div className="table-qr-modal__pair-guide-title">
                      {pendingPrint
                        ? "Connect printer to print label"
                        : "Connect your Niim Bot"}
                    </div>
                    <p className="table-qr-modal__pair-guide-text">
                      Power on the printer and keep it nearby. Chrome will open
                      a Bluetooth picker — choose your Niim Bot (often named{" "}
                      <strong>B1…</strong> or <strong>K3…</strong>).
                    </p>
                    <label className="table-qr-modal__published">
                      <input
                        type="checkbox"
                        checked={showAllBleDevices}
                        disabled={printerBusy}
                        onChange={(e) =>
                          setShowAllBleDevices(e.target.checked)
                        }
                      />
                      Printer not listed? Show all Bluetooth devices
                    </label>
                    <div className="table-qr-modal__pair-guide-actions">
                      <button
                        type="button"
                        className="table-qr-modal__btn table-qr-modal__btn--soft"
                        disabled={printerBusy}
                        onClick={() => {
                          setShowPairGuide(false);
                          setPendingPrint(false);
                          setShowAllBleDevices(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="table-qr-modal__btn table-qr-modal__btn--primary"
                        disabled={printerBusy}
                        onClick={handleConfirmPair}
                      >
                        {printerBusy ? "Waiting…" : "Open Bluetooth picker"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CollectionQrSection;
